import { z } from 'zod';
import type { MiddlewareModuleId } from './middlewares';

/**
 * Odoo Time Off (middleware `odoo`, docs/plans/odoo-timeoff.md; spec TIME-OFF-SPEC.md). Three
 * tiles, each its own module. Every answer is the signed-in person's own data (work email), except
 * Who is absent. Odoo's own ids (employee, allocation, leave, leave type) pass through: they
 * differ between databases, so the app never hard-codes one.
 */

export const ODOO_MODULE_IDS = [
  'odoo.leave',
  'odoo.request',
  'odoo.absences',
] as const satisfies readonly MiddlewareModuleId[];
export const odooModuleIdSchema = z.enum(ODOO_MODULE_IDS);
export type OdooModuleId = z.infer<typeof odooModuleIdSchema>;

export const ODOO_LEAVE_OVERVIEW_PATH = '/api/v1/odoo/leave/overview';
export const ODOO_LEAVE_ALLOCATION_PATH = '/api/v1/odoo/leave/allocations/:id';
export const ODOO_REQUEST_TYPES_PATH = '/api/v1/odoo/request/types';
/** POST: creates one `hr.leave` (plan §2.3). Its own segment: module routes need one. */
export const ODOO_REQUEST_LEAVE_PATH = '/api/v1/odoo/request/leave';
/** GET `?date=YYYY-MM-DD` (default today in the school time zone). Audited. */
export const ODOO_ABSENCES_DAY_PATH = '/api/v1/odoo/absences/day';

/** A positive Odoo record id (`id`, `*_id`). */
export const odooIdSchema = z.number().int().positive();
/** Days as Odoo counts them (0.5 for a half day; hours appear as fractions). */
const days = z.number().min(0);
const isoDate = z.iso.date();
const name = z.string().min(1).max(200);

/** `:id` in the allocation path: digits only, no sign, exponent or spaces. */
export const odooAllocationParamsSchema = z.object({
  id: z
    .string()
    .regex(/^[1-9][0-9]{0,9}$/)
    .transform(Number)
    .pipe(odooIdSchema),
});

/** The concrete Leave Detail path (throws on an id that is not a positive integer). */
export function odooLeaveAllocationPath(id: number): string {
  return ODOO_LEAVE_ALLOCATION_PATH.replace(':id', String(odooIdSchema.parse(id)));
}

/** Common to every answer: when it was read; `viewingAs` while developer view-as is on. */
const answerMeta = {
  fetchedAt: z.iso.datetime(),
  viewingAs: z.email().optional(),
};

/** ● full · ◐ morning · ◑ afternoon · ⏱ hours (spec §1.5, §2.2). */
export const ODOO_PARTS_OF_DAY = ['full_day', 'morning', 'afternoon', 'hours'] as const;
export const odooPartOfDaySchema = z.enum(ODOO_PARTS_OF_DAY);
export type OdooPartOfDay = z.infer<typeof odooPartOfDaySchema>;

/**
 * `hr.leave.state` as the app sees it (spec §4). The server maps Odoo's values: `confirm`,
 * the Director state (module setting `directorState`, ASB `dir_approve`), `validate1`,
 * `validate`, `refuse`, `cancel`.
 */
export const ODOO_LEAVE_STATUSES = [
  'to_approve',
  'director_approval',
  'final_approval',
  'approved',
  'refused',
  'cancelled',
] as const;
export const odooLeaveStatusSchema = z.enum(ODOO_LEAVE_STATUSES);
export type OdooLeaveStatus = z.infer<typeof odooLeaveStatusSchema>;

export const ODOO_LEAVE_STATUS_LABELS: Readonly<Record<OdooLeaveStatus, string>> = {
  to_approve: 'To Approve',
  director_approval: 'Director Approval',
  final_approval: 'Final Approval',
  approved: 'Approved',
  refused: 'Refused',
  cancelled: 'Cancelled',
};

const toNotBeforeFrom = (v: { from: string; to: string }) => v.to >= v.from;
const TO_BEFORE_FROM = { message: 'The end date is before the start date', path: ['to'] };

// ---- 1. My profile & leave: overview and allocation detail ----------------------------

export const odooPersonRefSchema = z.object({ id: odooIdSchema, name });
export type OdooPersonRef = z.infer<typeof odooPersonRefSchema>;

/**
 * The profile card: `hr.employee` matched by work email (404 none, 409 several). `approver` =
 * `leave_manager_id` (null: "not set", requests still possible). `directorEnabled` is false
 * when the Director field setting is empty or the field doesn't exist; then `director` is null
 * and the row hides.
 */
export const odooEmployeeSchema = z
  .object({
    id: odooIdSchema,
    name,
    workEmail: z.email(),
    department: z.string().max(200).nullable(),
    jobTitle: z.string().max(200).nullable(),
    approver: odooPersonRefSchema.nullable(),
    directorEnabled: z.boolean(),
    director: odooPersonRefSchema.nullable(),
  })
  .refine((v) => v.directorEnabled || v.director === null, {
    message: 'No director while the Director row is off',
    path: ['director'],
  });
export type OdooEmployee = z.infer<typeof odooEmployeeSchema>;

/**
 * One `hr.leave.allocation` valid today (school time zone). `left` = `number_of_days −
 * leaves_taken` (may go below 0). `booked` = approved leave not started yet; `waiting` = days
 * in requests still to approve. `nextBookedFrom` = start of the next booked leave (Home:
 * "12 booked in Dec"). `hasDetail`: taken, booked or waiting leave to show.
 */
export const odooAllocationSchema = z
  .object({
    allocationId: odooIdSchema,
    typeId: odooIdSchema,
    typeName: name,
    accrual: z.boolean(),
    validFrom: isoDate,
    /** Null = no end. */
    validTo: isoDate.nullable(),
    allocated: days,
    taken: days,
    booked: days,
    waiting: days,
    left: z.number(),
    nextBookedFrom: isoDate.nullable(),
    hasDetail: z.boolean(),
  })
  .refine((v) => v.validTo === null || v.validTo >= v.validFrom, {
    message: 'The allocation ends before it starts',
    path: ['validTo'],
  });
export type OdooAllocation = z.infer<typeof odooAllocationSchema>;

/**
 * GET /api/v1/odoo/leave/overview: the profile card, then the allocations (newest start
 * first, then by name). One Odoo employee lookup serves both.
 */
export const odooLeaveOverviewResponseSchema = z.object({
  employee: odooEmployeeSchema,
  timezone: z.string().min(1),
  today: isoDate,
  allocations: z.array(odooAllocationSchema),
  ...answerMeta,
});
export type OdooLeaveOverviewResponse = z.infer<typeof odooLeaveOverviewResponseSchema>;

/** One `hr.leave` row on Leave Detail (dates are `request_date_from` / `request_date_to`). */
export const odooLeaveEntrySchema = z
  .object({
    leaveId: odooIdSchema,
    from: isoDate,
    to: isoDate,
    days,
    partOfDay: odooPartOfDaySchema,
    description: z.string().max(1000),
    status: odooLeaveStatusSchema,
  })
  .refine(toNotBeforeFrom, TO_BEFORE_FROM);
export type OdooLeaveEntry = z.infer<typeof odooLeaveEntrySchema>;

/**
 * GET /api/v1/odoo/leave/allocations/:id (the person's own allocation; 404 otherwise).
 * Leaves of the same type starting inside the allocation's dates: waiting (to approve,
 * director, final), upcoming (approved, starts after today), taken (approved, started).
 */
export const odooLeaveAllocationResponseSchema = z.object({
  allocation: odooAllocationSchema,
  waiting: z.array(odooLeaveEntrySchema),
  upcoming: z.array(odooLeaveEntrySchema),
  /** Newest first. */
  taken: z.array(odooLeaveEntrySchema),
  ...answerMeta,
});
export type OdooLeaveAllocationResponse = z.infer<typeof odooLeaveAllocationResponseSchema>;

// ---- 2. Request leave: types, body, result ----------------------------------------------

/** Why a listed type can't be requested now. `on`: the date an expired allocation ended. */
export const odooUnusableReasonSchema = z.discriminatedUnion('code', [
  z.object({ code: z.literal('allocation_expired'), on: isoDate }),
  z.object({ code: z.literal('no_days_left') }),
  /** `support_document` types: "needs a document: request it in the Odoo portal". */
  z.object({ code: z.literal('needs_document') }),
]);
export type OdooUnusableReason = z.infer<typeof odooUnusableReasonSchema>;

/** A type from the person's approved allocations (portal flag permitting). */
export const odooRequestTypeSchema = z
  .object({
    typeId: odooIdSchema,
    name,
    left: z.number(),
    /** The type's unit allows half days (`request_unit` is not `day`). */
    halfDay: z.boolean(),
    usable: z.boolean(),
    reason: odooUnusableReasonSchema.nullable(),
  })
  .refine((v) => v.usable === (v.reason === null), {
    message: 'A type is usable exactly when it has no reason',
    path: ['reason'],
  });
export type OdooRequestType = z.infer<typeof odooRequestTypeSchema>;

/** GET /api/v1/odoo/request/types, in the Odoo portal's order. */
export const odooRequestTypesResponseSchema = z.object({
  types: z.array(odooRequestTypeSchema),
  ...answerMeta,
});
export type OdooRequestTypesResponse = z.infer<typeof odooRequestTypesResponseSchema>;

export const ODOO_DESCRIPTION_MAX = 500;

/**
 * POST /api/v1/odoo/request/leave. Strict: no employee (always the signed-in person) and no
 * document (owner, question 5). `to` defaults to `from`. A half day is one day with `period`
 * am/pm. The server also checks the type is usable now and needs no document.
 */
export const odooLeaveRequestBodySchema = z
  .strictObject({
    typeId: odooIdSchema,
    from: isoDate,
    to: isoDate.optional(),
    halfDay: z.boolean().default(false),
    period: z.enum(['am', 'pm']).optional(),
    description: z.string().trim().min(1).max(ODOO_DESCRIPTION_MAX),
  })
  .refine((v) => v.to === undefined || v.to >= v.from, TO_BEFORE_FROM)
  .refine((v) => !v.halfDay || ((v.to === undefined || v.to === v.from) && !!v.period), {
    message: 'A half day is one day, morning or afternoon',
    path: ['period'],
  })
  .refine((v) => v.halfDay || v.period === undefined, {
    message: 'Morning or afternoon only for a half day',
    path: ['period'],
  });
export type OdooLeaveRequestBody = z.infer<typeof odooLeaveRequestBodySchema>;

/** The created request read back (`hr.leave` `read`): Odoo worked out the days. */
export const odooLeaveRequestResultSchema = z
  .object({
    leaveId: odooIdSchema,
    typeId: odooIdSchema,
    typeName: name,
    from: isoDate,
    to: isoDate,
    days,
    partOfDay: odooPartOfDaySchema,
    status: odooLeaveStatusSchema,
    /** "Waiting for <approver>"; null when no approver is set. */
    approverName: name.nullable(),
  })
  .refine(toNotBeforeFrom, TO_BEFORE_FROM);
export type OdooLeaveRequestResult = z.infer<typeof odooLeaveRequestResultSchema>;

// ---- 3. Who is absent: GET /api/v1/odoo/absences/day ------------------------------------

/** Display and sort order (spec §2.1). */
export const ODOO_ABSENCE_CATEGORIES = [
  'sick',
  'vacation',
  'other',
  'working_elsewhere',
  'school_closure',
] as const;
export const odooAbsenceCategorySchema = z.enum(ODOO_ABSENCE_CATEGORIES);
export type OdooAbsenceCategory = z.infer<typeof odooAbsenceCategorySchema>;

export const odooAbsencesDayQuerySchema = z.object({ date: isoDate.optional() });
export type OdooAbsencesDayQuery = z.infer<typeof odooAbsencesDayQuerySchema>;

/** One approved leave overlapping the day. `letters`: 1, or 2 when first letters clash. */
export const odooAbsenceSchema = z
  .object({
    employeeId: odooIdSchema,
    name,
    workEmail: z.email().nullable(),
    category: odooAbsenceCategorySchema,
    leaveType: name,
    typeId: odooIdSchema,
    letters: z.string().min(1).max(2),
    partOfDay: odooPartOfDaySchema,
    from: isoDate,
    to: isoDate,
  })
  .refine(toNotBeforeFrom, TO_BEFORE_FROM);
export type OdooAbsence = z.infer<typeof odooAbsenceSchema>;

export const odooAbsenceCountsSchema = z.object({
  sick: z.number().int().min(0),
  vacation: z.number().int().min(0),
  other: z.number().int().min(0),
  working_elsewhere: z.number().int().min(0),
  school_closure: z.number().int().min(0),
});
export type OdooAbsenceCounts = z.infer<typeof odooAbsenceCountsSchema>;

/** Rows by category order, then name; `counts` always matches the rows. */
export const odooAbsencesDayResponseSchema = z
  .object({
    date: isoDate,
    timezone: z.string().min(1),
    counts: odooAbsenceCountsSchema,
    absent: z.array(odooAbsenceSchema),
    fetchedAt: z.iso.datetime(),
  })
  .refine(
    (v) =>
      ODOO_ABSENCE_CATEGORIES.every(
        (c) => v.counts[c] === v.absent.filter((a) => a.category === c).length,
      ),
    { message: 'Counts do not match the rows', path: ['counts'] },
  );
export type OdooAbsencesDayResponse = z.infer<typeof odooAbsencesDayResponseSchema>;
